import * as React from "react";
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { getSpreadsheetMetadata } from "../services/spreadsheet-metadata.service";
import { getUserData } from "../services/auth.service";
import { getOrganizationUsers } from "../services/users.service";
import { getWorkbookSetup, getReviewInfo, WorkbookSetupResponse, SpreadsheetReviewInfoResponse } from "../services/spreadsheets.service";
import { ExcelService } from "../services/excel.service";
import { getUserFriendlyErrorMessage } from "../utils/error-messages";
import { isStandaloneMode } from "../utils/standalone-mode";
import { MentionUser } from "../pages/discussions/components/richtext/types";
import { UserRole, getDefaultUserRole, isRoleBelow } from "../types/UserRole";
import { resolveMaxRole } from "../hooks/useMaxRole";

export interface WorksheetInfo {
  id: string;
  name: string;
}

export interface UserInfo {
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  id?: string;
  officeLocation?: string;
}

interface WorkflowIntelligenceContextType {
  spreadsheetId: string | null;
  userInfo: UserInfo | null;
  organizationUsers: MentionUser[];
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  workbookSetup: WorkbookSetupResponse | null;
  updateWorkbookSetup: (setup: WorkbookSetupResponse) => void;
  setSpreadsheetId: (id: string) => void;
  needsSetup: boolean;
  isLoading: boolean;
  error: string | null;
  refreshSpreadsheet: () => Promise<void>;
  worksheets: WorksheetInfo[];
  getWorksheetName: (sheetId: string) => string | null;
  getWorksheetId: (sheetName: string) => string | null;
  refreshWorksheets: () => Promise<void>;
  reviewStatus: string;
  reviewInfo: SpreadsheetReviewInfoResponse | null;
  setReviewInfo: (info: SpreadsheetReviewInfoResponse | null) => void;
  /**
   * True once a partner has signed off on the workbook. Partner sign is the
   * terminal state — status items get flipped to Partner Reviewed and the
   * completion certificate is generated. Use this to lock down actions on
   * pages like Discussions once the workbook is finalised.
   */
  isWorkbookSignedOff: boolean;
  /**
   * True when the addin is running outside Excel (plain browser tab, launched
   * via the Pop out button). Pages use this to hide Excel-writing controls,
   * skip worksheet-jump click handlers, and disable features that need the
   * Excel API (setCellValue, tab colours, `Excel.run`, etc.).
   */
  isStandalone: boolean;
}

const WorkflowIntelligenceContext = createContext<WorkflowIntelligenceContextType | undefined>(undefined);

interface WorkflowIntelligenceProviderProps {
  children: ReactNode;
}

/**
 * Top-level app state. Holds the active spreadsheet ID, the signed-in user,
 * organization users, role, workbook setup, the worksheet list, and review
 * status. Loaded once on mount; consumed by every page via useWorkflowIntelligence.
 */
export const WorkflowIntelligenceProvider: React.FC<WorkflowIntelligenceProviderProps> = ({ children }) => {
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [organizationUsers, setOrganizationUsers] = useState<MentionUser[]>([]);
  const [userRole, setUserRole] = useState<UserRole>(() => {
    // Load user role from localStorage on initialization.
    //
    // One-time migration: anyone still cached as "User" from when User was
    // the default gets bumped to the new default (Preparer). Gated by a
    // separate flag so the migration runs exactly once per browser. The flag
    // is then set regardless of which branch was taken, so we never
    // re-evaluate the migration condition on later loads — including for
    // users who deliberately re-pick User in Settings after the migration.
    const ROLE_MIGRATION_FLAG = 'userRole.migratedFromDefaultUser.v1';
    const savedRole = localStorage.getItem('userRole');
    const hasMigrated = !!localStorage.getItem(ROLE_MIGRATION_FLAG);

    if (!hasMigrated && savedRole === UserRole.User) {
      localStorage.setItem(ROLE_MIGRATION_FLAG, 'true');
      localStorage.setItem('userRole', UserRole.Preparer);
      return UserRole.Preparer;
    }

    localStorage.setItem(ROLE_MIGRATION_FLAG, 'true');
    return (savedRole as UserRole) || getDefaultUserRole();
  });
  const [workbookSetup, setWorkbookSetup] = useState<WorkbookSetupResponse | null>(null);
  const [worksheets, setWorksheets] = useState<WorksheetInfo[]>([]);
  const [reviewInfo, setReviewInfo] = useState<SpreadsheetReviewInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine if setup is needed (no client linked)
  const needsSetup = workbookSetup === null || (!workbookSetup.clientId && !workbookSetup.clientName);

  // Save user role to localStorage whenever it changes
  const handleSetUserRole = (role: UserRole) => {
    setUserRole(role);
    localStorage.setItem('userRole', role);
  };

  // In standalone mode we have no Excel context — `worksheets` stays empty,
  // and pages that use it for jump-to-sheet / reference-picker degrade
  // gracefully (they render "[Missing sheet]" or hide the affordance).
  const isStandalone = isStandaloneMode();

  // Load worksheets from Excel. No-op in standalone — Excel isn't reachable
  // from a plain browser tab.
  const loadWorksheets = useCallback(async () => {
    if (isStandalone) return;
    try {
      const worksheetInfos = await ExcelService.getAllWorksheetsInfo();
      setWorksheets(worksheetInfos);
    } catch (err) {
      console.error('Failed to load worksheets:', err);
    }
  }, [isStandalone]);

  // Helper to get worksheet name by ID
  const getWorksheetName = useCallback((sheetId: string): string | null => {
    const worksheet = worksheets.find(w => w.id === sheetId);
    return worksheet?.name ?? null;
  }, [worksheets]);

  // Helper to get worksheet ID by name
  const getWorksheetId = useCallback((sheetName: string): string | null => {
    const worksheet = worksheets.find(w => w.name === sheetName);
    return worksheet?.id ?? null;
  }, [worksheets]);

  const loadSpreadsheet = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch user data first to handle authentication
      // Then fetch spreadsheet metadata and organization users
      // User data is fetched first to avoid MSAL "interaction in progress" errors
      const userData = await getUserData();
      setUserInfo(userData);

      // Fetch spreadsheet metadata, organization users, and worksheets in parallel.
      //
      // `getSpreadsheetMetadata` handles the host branching internally: reads
      // from the workbook's custom XML part in Excel, from `?spreadsheetId=`
      // in standalone. Returns null for fresh / unlinked workbooks (Excel) or
      // for a missing / invalid URL param (standalone).
      //
      // Worksheets are Excel-only. In standalone the array stays empty and any
      // consumers (jump-to-sheet, reference picker) either hide themselves or
      // fall back to "[Missing sheet]".
      const worksheetsPromise = isStandalone
        ? Promise.resolve<WorksheetInfo[]>([])
        : ExcelService.getAllWorksheetsInfo().catch(err => {
            console.error('Failed to load worksheets during initialization:', err);
            return [];
          });

      const [id, users, worksheetInfos] = await Promise.all([
        getSpreadsheetMetadata(),
        getOrganizationUsers(false).catch(err => {
          // Log error but don't fail initialization if users fetch fails
          console.error('Failed to load organization users during initialization:', err);
          return [];
        }),
        worksheetsPromise,
      ]);

      setSpreadsheetId(id);
      setOrganizationUsers(users);
      setWorksheets(worksheetInfos);

      // Only fetch workbook setup / review info if we actually have an ID —
      // both endpoints are keyed on it. Null id means "workbook not yet linked",
      // and needsSetup falls out naturally from workbookSetup staying null.
      if (id) {
        const [setup, reviewData] = await Promise.all([
          getWorkbookSetup(id).catch(err => {
            console.error('Failed to load workbook setup during initialization:', err);
            return null;
          }),
          getReviewInfo(id).catch(err => {
            console.error('Failed to load review info during initialization:', err);
            return null;
          }),
        ]);
        setWorkbookSetup(setup);
        setReviewInfo(reviewData);
      } else {
        setWorkbookSetup(null);
        setReviewInfo(null);
      }
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      console.error('Failed to initialize spreadsheet:', err);
      setError(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize spreadsheet metadata on mount
  useEffect(() => {
    loadSpreadsheet();
  }, []);

  // One-time role auto-init based on the caller's DH group membership.
  //
  // The backend's /me/role endpoint returns "Partner" or "Manager" ONLY when
  // the user is in the corresponding security group; everyone else gets
  // "Reviewer" as a FALLBACK. That fallback string represents "this user's
  // ceiling is Reviewer", NOT "this user is a Reviewer". v2 conflated the
  // two and wrongly promoted every non-Partner/non-Manager (i.e. every real
  // Preparer) to Reviewer, which let ribbon sign-off flag their sheets as
  // Quality Reviewed. This v3 fixes that, and repairs the v2 damage.
  //
  // Rules (using the raw backend string, not normaliseBackendRoleToUserRole,
  // because we need to tell "Manager" apart from the "Reviewer" fallback):
  //   - "Partner"  → set Partner (upgrade; never downgrade below current).
  //   - "Manager"  → set Reviewer, unless the user is already Partner.
  //   - "Reviewer" (fallback) → force Preparer, unless the user is Partner.
  //     This is the intentional repair for users v2 pushed to Reviewer; it
  //     also downgrades anyone who deliberately picked Reviewer in Settings
  //     without being a Manager (a small population — they can re-pick).
  //   - Anything else → skip (unknown backend value).
  useEffect(() => {
    if (!userInfo) return;
    const AUTO_INIT_FLAG = 'userRole.autoInitialised.v3';
    if (localStorage.getItem(AUTO_INIT_FLAG)) return;
    let cancelled = false;
    (async () => {
      try {
        const email = userInfo.mail || userInfo.userPrincipalName;
        const backendRole = (await resolveMaxRole(email)).toLowerCase();
        if (cancelled) return;

        if (backendRole === "partner") {
          if (isRoleBelow(userRole, UserRole.Partner)) {
            handleSetUserRole(UserRole.Partner);
          }
        } else if (backendRole === "manager") {
          // Real Manager — cap at Reviewer, don't touch Partners (defensive
          // against transient partner-lookup errors seen earlier).
          if (userRole !== UserRole.Partner && isRoleBelow(userRole, UserRole.Reviewer)) {
            handleSetUserRole(UserRole.Reviewer);
          }
        } else if (backendRole === "reviewer") {
          // Backend fallback for non-Partner/non-Manager. Force Preparer to
          // repair v2 damage. Keep Partner if the current localStorage says
          // Partner — a "Reviewer" response for an actual Partner is almost
          // always a transient partner-lookup miss, and we don't want to
          // demote real Partners during a cache blip.
          if (userRole !== UserRole.Partner) {
            handleSetUserRole(UserRole.Preparer);
          }
        }
        // Unrecognised backend values: skip the write, still set the flag
        // so we don't retry indefinitely.
        localStorage.setItem(AUTO_INIT_FLAG, 'true');
      } catch (error) {
        console.error("Failed to auto-initialise user role from backend max:", error);
        // Don't set the flag on network error — retry next sign-in.
      }
    })();
    return () => { cancelled = true; };
  }, [userInfo]);

  // Register for worksheet structural change events (add, delete, rename)
  // to keep the local worksheet mapping synchronized. Skipped in standalone —
  // there's no Excel to subscribe to.
  useEffect(() => {
    if (isStandalone) return;

    let unsubscribe: (() => Promise<void>) | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const debouncedRefresh = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        loadWorksheets();
      }, 300);
    };

    const registerEvents = async () => {
      try {
        unsubscribe = await ExcelService.onWorksheetCollectionChanged(debouncedRefresh);
      } catch (error) {
        console.error("Failed to register worksheet change events:", error);
      }
    };

    registerEvents();

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (unsubscribe) {
        unsubscribe().catch((error) => {
          console.error("Failed to unregister worksheet change events:", error);
        });
      }
    };
  }, [loadWorksheets, isStandalone]);

  const refreshSpreadsheet = async () => {
    await loadSpreadsheet();
  };

  const updateWorkbookSetup = (setup: WorkbookSetupResponse) => {
    setWorkbookSetup(setup);
  };

  const reviewStatus = reviewInfo?.status ?? "Draft";
  const isWorkbookSignedOff = !!reviewInfo?.signingPartnerEmail;

  const value: WorkflowIntelligenceContextType = {
    spreadsheetId,
    userInfo,
    organizationUsers,
    userRole,
    setUserRole: handleSetUserRole,
    workbookSetup,
    updateWorkbookSetup,
    setSpreadsheetId,
    needsSetup,
    isLoading,
    error,
    refreshSpreadsheet,
    worksheets,
    getWorksheetName,
    getWorksheetId,
    refreshWorksheets: loadWorksheets,
    reviewStatus,
    reviewInfo,
    setReviewInfo,
    isWorkbookSignedOff,
    isStandalone,
  };

  return (
    <WorkflowIntelligenceContext.Provider value={value}>
      {children}
    </WorkflowIntelligenceContext.Provider>
  );
};

/**
 * Hook to access the spreadsheet context
 * @throws Error if used outside of WorkflowIntelligenceProvider
 */
export const useWorkflowIntelligence = (): WorkflowIntelligenceContextType => {
  const context = useContext(WorkflowIntelligenceContext);
  if (context === undefined) {
    throw new Error("useWorkflowIntelligence must be used within a WorkflowIntelligenceProvider");
  }
  return context;
};
